// In-App Diagnostic Profiler for Performance Metrics
// Records execution times of mathematical algorithms, 3D buffer uploads, useFrame hooks, and React renders.

import { DBG_ENABLED } from './dbgFlag';
import { jankBlock } from './jankRecorder';
import { p1Block } from './p1Probe';

interface ProfilerSample {
  name: string;
  durationMs: number;
  timestamp: number;
  meta?: any;
}

class AppPerformanceProfiler {
  private samples: Map<string, number[]> = new Map();
  private maxSamples = 120; // Last 120 ticks (~60 seconds at 2fps)
  private peakSamples: Map<string, number> = new Map();
  private lastSummaryTime = 0;
  private frameCount = 0;
  private lastFpsTime = performance.now();
  private currentFps = 60;

  // Log active state
  public isEnabled = true;

  record(name: string, durationMs: number, meta?: any) {
    if (!this.isEnabled) return;
    
    if (!this.samples.has(name)) {
      this.samples.set(name, []);
      this.peakSamples.set(name, 0);
    }

    const list = this.samples.get(name)!;
    list.push(durationMs);
    if (list.length > this.maxSamples) list.shift();

    if (DBG_ENABLED) p1Block(name, durationMs);
    if (DBG_ENABLED && durationMs >= 4) jankBlock(name, durationMs); // diagnóstico (?dbg=1): blocos lentos viram causa de quadro longo

    const currentPeak = this.peakSamples.get(name) || 0;
    if (durationMs > currentPeak) {
      this.peakSamples.set(name, durationMs);
    }
  }

  time<T>(name: string, fn: () => T, meta?: any): T {
    if (!this.isEnabled) return fn();
    const start = performance.now();
    try {
      return fn();
    } finally {
      const dur = performance.now() - start;
      this.record(name, dur, meta);
    }
  }

  async timeAsync<T>(name: string, fn: () => Promise<T>, meta?: any): Promise<T> {
    if (!this.isEnabled) return await fn();
    const start = performance.now();
    try {
      return await fn();
    } finally {
      const dur = performance.now() - start;
      this.record(name, dur, meta);
    }
  }

  tickFps() {
    this.frameCount++;
    const now = performance.now();
    if (now - this.lastFpsTime >= 1000) {
      this.currentFps = Math.round((this.frameCount * 1000) / (now - this.lastFpsTime));
      this.frameCount = 0;
      this.lastFpsTime = now;
      this.checkAndLogSummary(now);
    }
  }

  private checkAndLogSummary(now: number) {
    // Print comprehensive summary every 5 seconds
    if (now - this.lastSummaryTime >= 5000) {
      this.lastSummaryTime = now;
      this.printSummary();
    }
  }

  public getSummary() {
    const stats: Record<string, { avgMs: number; peakMs: number; count: number }> = {};
    for (const [name, list] of this.samples.entries()) {
      if (list.length === 0) continue;
      const sum = list.reduce((a, b) => a + b, 0);
      const avg = sum / list.length;
      const peak = this.peakSamples.get(name) || 0;
      stats[name] = {
        avgMs: Number(avg.toFixed(2)),
        peakMs: Number(peak.toFixed(2)),
        count: list.length,
      };
    }
    return {
      fps: this.currentFps,
      stats,
    };
  }

  public printSummary() {
    const summary = this.getSummary();
    console.group(`[PROFILER DIAGNOSTIC] FPS: ${summary.fps} | Samples recorded:`);
    console.table(summary.stats);
    console.groupEnd();
  }
}

export const appProfiler = new AppPerformanceProfiler();

// Expose globally to window for browser inspection
if (typeof window !== 'undefined') {
  (window as any).__APP_PROFILER__ = appProfiler;
}
